import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { API } from "../../data/content";

const initial = { from_name: "", from_email: "", subject: "", message: "", website: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState(null); // 'ok' | 'error' | null
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("ok");
      setForm(initial);
    } catch (err) {
      setStatus(err?.response?.status === 429 ? "rate" : "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-5">
                / Contact
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
                Questions or feedback?
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                Drop us a note and we'll get back to you by email. For in-app
                issues, you can also use the feedback option inside ROLL.OPS.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8"
              >
                {/* Honeypot — hidden from real users */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={update("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">
                      Name
                    </label>
                    <input
                      data-testid="contact-name"
                      required
                      maxLength={120}
                      value={form.from_name}
                      onChange={update("from_name")}
                      className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">
                      Reply-to email
                    </label>
                    <input
                      data-testid="contact-email"
                      required
                      type="email"
                      value={form.from_email}
                      onChange={update("from_email")}
                      className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block font-mono text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">
                    Subject
                  </label>
                  <input
                    data-testid="contact-subject"
                    required
                    maxLength={200}
                    value={form.subject}
                    onChange={update("subject")}
                    className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none transition-colors"
                    placeholder="What's this about?"
                  />
                </div>

                <div className="mt-5">
                  <label className="block font-mono text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">
                    Message
                  </label>
                  <textarea
                    data-testid="contact-message"
                    required
                    maxLength={5000}
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none transition-colors resize-y"
                    placeholder="Tell us what's on your mind…"
                  />
                  <p className="mt-1 text-right font-mono text-[11px] text-zinc-600">
                    {form.message.length}/5000
                  </p>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold bg-white text-black hover:bg-cyan-300 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </button>

                  {status === "ok" && (
                    <span data-testid="contact-success" className="inline-flex items-center gap-2 text-sm text-green-400">
                      <CheckCircle2 className="h-4 w-4" /> Thanks — your message was sent.
                    </span>
                  )}
                  {status === "error" && (
                    <span data-testid="contact-error" className="inline-flex items-center gap-2 text-sm text-red-400">
                      <AlertCircle className="h-4 w-4" /> Something went wrong. Please try again.
                    </span>
                  )}
                  {status === "rate" && (
                    <span data-testid="contact-rate" className="inline-flex items-center gap-2 text-sm text-amber-400">
                      <AlertCircle className="h-4 w-4" /> Too many messages — please try again later.
                    </span>
                  )}
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
